import { CSSProperties } from 'react';
import { LoopShape } from '../MainView/MainLogo';

// Leaders from each loop segment to its keynote bubble, in the logo's 2048 coordinate space.
// side: where the label sits relative to its bubble.
const KEYNOTES = [
	{ n: 1, anchor: [600, 398], bubble: [330, 250], side: 'right' },
	{ n: 2, anchor: [110, 930], bubble: [-40, 1190], side: 'right' },
	{ n: 3, anchor: [640, 1010], bubble: [560, 1300], side: 'left' },
	{ n: 4, anchor: [2010, 1120], bubble: [2100, 820], side: 'left' },
	{ n: 5, anchor: [1480, 1528], bubble: [1640, 1700], side: 'right' },
	{ n: 6, anchor: [1015, 790], bubble: [1270, 520], side: 'right' },
] as const;

const R = 44;

interface LoopFigureProps {
	active: number;
	labels: string[];
	span: string;
	onSelect?: (n: number) => void;
	showLabels?: boolean;
	animateIn?: boolean;
	className?: string;
}

export default function LoopFigure({ active, labels, span, onSelect, showLabels = true, animateIn = true, className = '' }: LoopFigureProps) {
	const plot = (delay: number) => (animateIn ? { className: 'plot-line', style: { ['--delay' as string]: `${delay}s` } as CSSProperties } : {});

	return (
		<svg viewBox='-120 170 2300 1690' className={className} role='img' aria-label={labels.map((l, i) => `${i + 1}. ${l}`).join(', ')}>
			<LoopShape state={active} />

			{KEYNOTES.map(({ n, anchor, bubble, side }, i) => {
				const isActive = n === active;
				const [ax, ay] = anchor;
				const [bx, by] = bubble;
				// Leader stops at the bubble's edge.
				const dx = ax - bx;
				const dy = ay - by;
				const len = Math.hypot(dx, dy);
				const ex = bx + (dx / len) * R;
				const ey = by + (dy / len) * R;
				const labelX = side === 'right' ? bx + R + 22 : bx - R - 22;
				return (
					<g
						key={n}
						className={onSelect ? 'cursor-pointer' : undefined}
						onClick={onSelect ? () => onSelect(n) : undefined}
						onMouseEnter={onSelect ? () => onSelect(n) : undefined}>
						<line x1={ex} y1={ey} x2={ax} y2={ay} pathLength={1} strokeWidth={1.25} vectorEffect='non-scaling-stroke' className={`transition-[stroke] duration-300 ${isActive ? 'stroke-redline' : 'stroke-ink-faint'} ${plot(0.25 + i * 0.08).className ?? ''}`} style={plot(0.25 + i * 0.08).style} />
						<circle cx={ax} cy={ay} r={9} className={`transition-[fill] duration-300 ${isActive ? 'fill-redline' : 'fill-ink-soft'}`} />
						<circle cx={bx} cy={by} r={R} strokeWidth={1.25} vectorEffect='non-scaling-stroke' className={`transition-[fill,stroke] duration-300 ${isActive ? 'fill-redline stroke-redline' : 'fill-sheet stroke-ink-soft'}`} />
						<text x={bx} y={by + 17} textAnchor='middle' fontSize={50} fontWeight={700} className={`transition-[fill] duration-300 ${isActive ? 'fill-sheet-deep' : 'fill-ink'}`}>
							{n}
						</text>
						{showLabels && (
							<text x={labelX} y={by + 16} textAnchor={side === 'right' ? 'start' : 'end'} fontSize={46} fontWeight={600} letterSpacing={4} className={`hidden uppercase transition-[fill] duration-300 sm:block ${isActive ? 'fill-redline' : 'fill-ink-soft'}`} style={{ fontStretch: '72%' }}>
								{labels[n - 1]}
							</text>
						)}
					</g>
				);
			})}

			{/* Overall dimension: the loop spans the whole lifecycle. */}
			<g className='stroke-ink-faint' strokeWidth={1.25} vectorEffect='non-scaling-stroke'>
				<line x1={21} y1={1800} x2={2028} y2={1800} vectorEffect='non-scaling-stroke' pathLength={1} {...plot(0.1)} />
				<line x1={21} y1={1560} x2={21} y2={1830} vectorEffect='non-scaling-stroke' strokeDasharray='10 14' />
				<line x1={2028} y1={1240} x2={2028} y2={1830} vectorEffect='non-scaling-stroke' strokeDasharray='10 14' />
				<line x1={0} y1={1821} x2={42} y2={1779} vectorEffect='non-scaling-stroke' />
				<line x1={2007} y1={1821} x2={2049} y2={1779} vectorEffect='non-scaling-stroke' />
			</g>
			<rect x={1024 - 330} y={1772} width={660} height={56} className='hidden fill-sheet sm:block' />
			<text x={1024} y={1816} textAnchor='middle' fontSize={44} fontWeight={600} letterSpacing={4} className='hidden fill-ink-soft uppercase sm:block' style={{ fontStretch: '72%' }}>
				{span}
			</text>
		</svg>
	);
}
